// Field work for one survey: which enumerators may collect it, which named
// respondents each enumerator visits, and random assignment to study groups.
import { Check, ChevronLeft, ChevronRight, ClipboardList, Search, Shuffle, UserCheck, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../../api/client';
import type { RespondentRecord } from '../../api/types';
import { useI18n } from '../../i18n';
import { CollapsibleCard } from '../CollapsibleCard';
import { PageLoader, useToast } from '../ui';

type Enumerator = { userId: string; name: string; email: string };
const PAGE = 25;

/** Enumerators allowed to download and sync this survey, picked as chips. */
export function EnumeratorAssignments({ surveyId, onChange }: { surveyId: string; onChange?: (assigned: Enumerator[]) => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const [available, setAvailable] = useState<Enumerator[] | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [saved, setSaved] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.enumerators(), api.surveyEnumerators(surveyId)]).then(
      ([all, assigned]) => {
        setAvailable(all.enumerators);
        const ids = assigned.enumerators.map((e) => e.userId);
        setSelected(ids);
        setSaved(ids);
      },
      (e) => toast(e instanceof ApiError ? e.message : t('enumerators.load_failed'), 'error'),
    );
  }, [surveyId, toast, t]);

  async function save() {
    setBusy(true);
    try {
      const result = await api.setSurveyEnumerators(surveyId, selected);
      const ids = result.enumerators.map((e) => e.userId);
      setSelected(ids);
      setSaved(ids);
      onChange?.(result.enumerators);
      toast(t('enumerators.saved'));
    } catch (e) {
      toast(e instanceof ApiError ? e.message : t('common.action_failed'), 'error');
    } finally {
      setBusy(false);
    }
  }

  const toggle = (id: string) => setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const dirty = [...selected].sort().join() !== [...saved].sort().join();
  const q = query.trim().toLowerCase();
  const shown = (available ?? []).filter((e) => !q || `${e.name} ${e.email}`.toLowerCase().includes(q));

  return (
    <div className="card">
      <div className="card-head">
        <div className="tile">
          <UserCheck />
        </div>
        <div className="grow">
          <h2>{t('enumerators.title')}</h2>
          <div className="sub">{t('enumerators.help')}</div>
        </div>
      </div>
      <div className="card-pad stack gap-12" style={{ paddingTop: 0 }}>
        {available === null ? (
          <PageLoader />
        ) : available.length === 0 ? (
          <p className="small muted">{t('enumerators.none')}</p>
        ) : (
          <>
            {available.length > 8 && (
              <div className="search">
                <Search />
                <input className="input" value={query} placeholder={t('field.search_enumerators')} aria-label={t('field.search_enumerators')} onChange={(e) => setQuery(e.target.value)} />
              </div>
            )}
            <div className="chip-pick" role="group" aria-label={t('enumerators.title')}>
              {shown.map((e) => {
                const on = selected.includes(e.userId);
                return (
                  <button key={e.userId} type="button" className={`chip ${on ? 'active' : ''}`} aria-pressed={on} disabled={busy} title={e.email} onClick={() => toggle(e.userId)}>
                    {on && <Check className="icon-sm" />}
                    {e.name || e.email}
                  </button>
                );
              })}
              {shown.length === 0 && <span className="small muted">{t('field.no_match')}</span>}
            </div>
            <div className="card-foot">
              <span className="small muted">
                {t('enumerators.selected', {
                  count: selected.length,
                  total: available.length,
                })}
                {dirty && ` · ${t('field.unsaved')}`}
              </span>
              <button className="btn primary" onClick={() => void save()} disabled={busy || !dirty}>
                {t('enumerators.save')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

type Filter = 'all' | 'unassigned' | 'assigned';

/** Which enumerator visits each named respondent: a searchable, paged table with bulk assignment. */
export function RespondentAssignments({ surveyId }: { surveyId: string }) {
  const { t } = useI18n();
  const toast = useToast();
  const [respondents, setRespondents] = useState<RespondentRecord[] | null>(null);
  const [enumerators, setEnumerators] = useState<Enumerator[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<Record<string, string>>({});
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [byEnumerator, setByEnumerator] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [bulkTarget, setBulkTarget] = useState('');
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.respondents(), api.enumerators(), api.surveyRespondentAssignments(surveyId)]).then(
      ([r, e, a]) => {
        setRespondents(r.respondents.filter((x) => x.active));
        setEnumerators(e.enumerators);
        const m = Object.fromEntries(a.assignments.map((x) => [x.respondentId, x.enumeratorId]));
        setMapping(m);
        setSaved(m);
      },
      (e) => toast(e instanceof ApiError ? e.message : t('enumerators.load_failed'), 'error'),
    );
  }, [surveyId, toast, t]);

  const names = useMemo(() => new Map(enumerators.map((e) => [e.userId, e.name || e.email])), [enumerators]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (respondents ?? []).filter((r) => {
      const who = mapping[r.id] ?? '';
      if (filter === 'unassigned' && who) return false;
      if (filter === 'assigned' && !who) return false;
      if (byEnumerator && who !== byEnumerator) return false;
      return !q || `${r.name} ${r.externalId}`.toLowerCase().includes(q);
    });
  }, [respondents, mapping, query, filter, byEnumerator]);

  // Reset paging when the filters change; keep it in range when rows disappear.
  useEffect(() => setPage(0), [query, filter, byEnumerator]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const current = Math.min(page, pages - 1);
  const rows = filtered.slice(current * PAGE, current * PAGE + PAGE);

  if (respondents === null) {
    return (
      <div className="card">
        <PageLoader />
      </div>
    );
  }

  const assigned = respondents.filter((r) => mapping[r.id]).length;
  const changed = respondents.filter((r) => (mapping[r.id] ?? '') !== (saved[r.id] ?? '')).length;
  const allOnPage = rows.length > 0 && rows.every((r) => picked.has(r.id));
  const togglePick = (id: string) =>
    setPicked((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const togglePage = () =>
    setPicked((cur) => {
      const next = new Set(cur);
      for (const r of rows) {
        if (allOnPage) next.delete(r.id);
        else next.add(r.id);
      }
      return next;
    });
  const applyBulk = () => {
    setMapping((m) => {
      const next = { ...m };
      for (const id of picked) {
        if (bulkTarget) next[id] = bulkTarget;
        else delete next[id];
      }
      return next;
    });
    setPicked(new Set());
  };

  async function save() {
    setBusy(true);
    try {
      const a = await api.setSurveyRespondentAssignments(
        surveyId,
        Object.entries(mapping)
          .filter(([, e]) => e)
          .map(([respondentId, enumeratorId]) => ({
            respondentId,
            enumeratorId,
          })),
      );
      const m = Object.fromEntries(a.assignments.map((x) => [x.respondentId, x.enumeratorId]));
      setMapping(m);
      setSaved(m);
      toast(t('respondents.assignments_saved'));
    } catch (e) {
      toast(e instanceof ApiError ? e.message : t('common.action_failed'), 'error');
    } finally {
      setBusy(false);
    }
  }

  const counts: Record<Filter, number> = {
    all: respondents.length,
    unassigned: respondents.length - assigned,
    assigned,
  };

  return (
    <div className="card">
      <div className="card-head">
        <div className="tile">
          <ClipboardList />
        </div>
        <div className="grow">
          <h2>{t('respondents.assignments_title')}</h2>
          <div className="sub">{t('respondents.assignments_help')}</div>
        </div>
      </div>
      <div className="card-pad stack gap-12" style={{ paddingTop: 0 }}>
        {respondents.length === 0 ? (
          <p className="small muted">{t('respondents.none')}</p>
        ) : (
          <>
            <div className="assign-toolbar">
              <div className="search grow">
                <Search />
                <input className="input" value={query} placeholder={t('field.search_respondents')} aria-label={t('field.search_respondents')} onChange={(e) => setQuery(e.target.value)} />
              </div>
              <div className="row gap-4 wrap">
                {(['all', 'unassigned', 'assigned'] as Filter[]).map((f) => (
                  <button key={f} type="button" className={`chip ${filter === f ? 'active' : ''} ${f === 'unassigned' && counts.unassigned ? 'warn' : ''}`} onClick={() => setFilter(f)}>
                    {t(`field.filter_${f}`)} <span className="count">{counts[f]}</span>
                  </button>
                ))}
              </div>
              <select className="select assign-filter" value={byEnumerator} aria-label={t('field.filter_enumerator')} onChange={(e) => setByEnumerator(e.target.value)}>
                <option value="">{t('field.any_enumerator')}</option>
                {enumerators.map((e) => (
                  <option key={e.userId} value={e.userId}>
                    {e.name || e.email}
                  </option>
                ))}
              </select>
            </div>

            {picked.size > 0 && (
              <div className="bulk-bar">
                <strong>{t('field.picked', { count: picked.size })}</strong>
                <select className="select" value={bulkTarget} aria-label={t('field.assign_to')} onChange={(e) => setBulkTarget(e.target.value)}>
                  <option value="">{t('respondents.unassigned')}</option>
                  {enumerators.map((e) => (
                    <option key={e.userId} value={e.userId}>
                      {e.name || e.email}
                    </option>
                  ))}
                </select>
                <button className="btn sm primary" onClick={applyBulk}>
                  {bulkTarget ? t('field.assign_picked') : t('field.unassign_picked')}
                </button>
                <button className="btn sm ghost" onClick={() => setPicked(new Set())}>
                  <X /> {t('field.clear_pick')}
                </button>
              </div>
            )}

            <div className="table-wrap">
              <table className="table assign-table">
                <thead>
                  <tr>
                    <th className="col-check">
                      <input type="checkbox" checked={allOnPage} aria-label={t('field.pick_page')} onChange={togglePage} />
                    </th>
                    <th>{t('respondents.name')}</th>
                    <th className="assign-id">{t('field.external_id')}</th>
                    <th>{t('field.enumerator')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const changedRow = (mapping[r.id] ?? '') !== (saved[r.id] ?? '');
                    return (
                      <tr key={r.id} className={picked.has(r.id) ? 'picked' : ''}>
                        <td className="col-check">
                          <input type="checkbox" checked={picked.has(r.id)} aria-label={r.name} onChange={() => togglePick(r.id)} />
                        </td>
                        <td>
                          <span className="ellipsis block">{r.name}</span>
                          {r.externalId && <span className="small muted assign-id-inline">{r.externalId}</span>}
                        </td>
                        <td className="muted small assign-id">{r.externalId || '—'}</td>
                        <td>
                          <select
                            className={`select sm ${mapping[r.id] ? '' : 'sel-unset'} ${changedRow ? 'sel-changed' : ''}`}
                            aria-label={t('field.enumerator_for', {
                              name: r.name,
                            })}
                            value={mapping[r.id] ?? ''}
                            disabled={busy}
                            onChange={(e) => setMapping({ ...mapping, [r.id]: e.target.value })}
                          >
                            <option value="">{t('respondents.unassigned')}</option>
                            {mapping[r.id] && !names.has(mapping[r.id]!) && <option value={mapping[r.id]}>{t('field.unknown_enumerator')}</option>}
                            {enumerators.map((x) => (
                              <option key={x.userId} value={x.userId}>
                                {x.name || x.email}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={4} className="muted small" style={{ textAlign: 'center', padding: 18 }}>
                        {t('field.no_match')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="card-foot">
              <span className="small muted">
                {t('respondents.assigned_count', {
                  count: assigned,
                  total: respondents.length,
                })}
                {changed > 0 && ` · ${t('field.changed', { count: changed })}`}
              </span>
              <div className="row gap-8">
                {pages > 1 && (
                  <div className="row gap-4 small muted nowrap">
                    <button className="btn ghost sm icon" disabled={current === 0} aria-label={t('field.prev_page')} onClick={() => setPage(current - 1)}>
                      <ChevronLeft />
                    </button>
                    {t('field.page_of', { page: current + 1, pages })}
                    <button className="btn ghost sm icon" disabled={current >= pages - 1} aria-label={t('field.next_page')} onClick={() => setPage(current + 1)}>
                      <ChevronRight />
                    </button>
                  </div>
                )}
                <button className="btn primary" disabled={busy || changed === 0} onClick={() => void save()}>
                  {t('enumerators.save')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

type GroupQuota = { name: string; quotas: Record<string, number> };

export function GroupRandomizer({ surveyId }: { surveyId: string }) {
  const { t } = useI18n();
  const toast = useToast();
  const [stratifyBy, setStratifyBy] = useState<'none' | 'gender'>('gender');
  const [genderCounts, setGenderCounts] = useState<Record<string, number>>({});
  const [strataCounts, setStrataCounts] = useState<Record<string, number>>({});
  const [groups, setGroups] = useState<GroupQuota[]>([]);
  const [respondentNames, setRespondentNames] = useState<Record<string, string>>({});
  const [currentAssignments, setCurrentAssignments] = useState<{ respondentId: string; groupName: string; stratum: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([api.respondents(), api.surveyRespondentAssignments(surveyId), api.surveyGroupAssignments(surveyId)]).then(
      ([roster, assigned, saved]) => {
        const genderById = new Map(roster.respondents.map((r) => [r.id, r.gender || 'unspecified']));
        setRespondentNames(Object.fromEntries(roster.respondents.map((r) => [r.id, `${r.name}${r.externalId ? ` · ${r.externalId}` : ''}`])));
        const assignedStrata: Record<string, number> = {};
        for (const a of assigned.assignments) {
          const stratum = genderById.get(a.respondentId) ?? 'unspecified';
          assignedStrata[stratum] = (assignedStrata[stratum] ?? 0) + 1;
        }
        const useGender = saved.assignments.some((a) => a.stratum !== 'all');
        setCurrentAssignments(saved.assignments);
        const selectedStratification = useGender ? 'gender' : 'none';
        const currentStrata = useGender ? assignedStrata : { all: Object.values(assignedStrata).reduce((n, x) => n + x, 0) };
        setGenderCounts(assignedStrata);
        setStratifyBy(selectedStratification);
        setStrataCounts(currentStrata);
        if (saved.assignments.length) {
          const existing = new Map<string, Record<string, number>>();
          for (const a of saved.assignments) {
            const quotas = existing.get(a.groupName) ?? {};
            quotas[a.stratum] = (quotas[a.stratum] ?? 0) + 1;
            existing.set(a.groupName, quotas);
          }
          setGroups(Array.from(existing, ([name, quotas]) => ({ name, quotas })));
        } else {
          const keys = Object.keys(currentStrata);
          const initial = ['Control', 'Treatment'].map((name, i) => ({
            name,
            quotas: Object.fromEntries(keys.map((key) => [key, Math.floor((currentStrata[key] ?? 0) / 2) + (i === 0 && (currentStrata[key] ?? 0) % 2 ? 1 : 0)])),
          }));
          setGroups(initial);
        }
        setLoaded(true);
      },
      (e) => toast(e instanceof ApiError ? e.message : t('groups.load_failed'), 'error'),
    );
  }, [surveyId, toast, t]);

  function changeStratification(next: 'none' | 'gender') {
    const counts = next === 'none' ? { all: Object.values(genderCounts).reduce((n, x) => n + x, 0) } : genderCounts;
    setStratifyBy(next);
    setStrataCounts(counts);
    setGroups((current) =>
      current.map((group, i) => ({
        ...group,
        quotas: Object.fromEntries(Object.entries(counts).map(([key, n]) => [key, Math.floor(n / current.length) + (i < n % current.length ? 1 : 0)])),
      })),
    );
  }

  function addGroup() {
    if (groups.length >= 20) return;
    const name = `Group ${groups.length + 1}`;
    setGroups((current) => [
      ...current,
      {
        name,
        quotas: Object.fromEntries(Object.keys(strataCounts).map((key) => [key, 0])),
      },
    ]);
  }

  async function randomize() {
    setBusy(true);
    try {
      const result = await api.randomizeSurveyGroups(surveyId, stratifyBy, groups);
      setCurrentAssignments(result.assignments);
      const countsByGroup = new Map<string, Record<string, number>>();
      for (const a of result.assignments) {
        const quotas = countsByGroup.get(a.groupName) ?? {};
        quotas[a.stratum] = (quotas[a.stratum] ?? 0) + 1;
        countsByGroup.set(a.groupName, quotas);
      }
      setGroups(Array.from(countsByGroup, ([name, quotas]) => ({ name, quotas })));
      toast(t('groups.randomized'));
    } catch (e) {
      toast(e instanceof ApiError ? e.message : t('common.action_failed'), 'error');
    } finally {
      setBusy(false);
    }
  }

  const quotaTotals = Object.fromEntries(Object.keys(strataCounts).map((key) => [key, groups.reduce((n, group) => n + (group.quotas[key] ?? 0), 0)]));
  const groupSummary = currentAssignments.length
    ? t('groups.summary', {
        groups: new Set(currentAssignments.map((a) => a.groupName)).size,
        count: currentAssignments.length,
      })
    : t('groups.not_yet');
  return (
    <CollapsibleCard storageKey="survey.field.open.groups" defaultOpen={false} icon={<Shuffle />} title={t('groups.title')} sub={loaded ? groupSummary : undefined}>
      <div className="card-pad stack gap-12" style={{ paddingTop: 0 }}>
        <p className="hint" style={{ margin: 0 }}>
          {t('groups.help')}
        </p>
        {!loaded ? (
          <PageLoader />
        ) : Object.values(strataCounts).reduce((n, x) => n + x, 0) === 0 ? (
          <p className="small muted">{t('groups.assign_respondents_first')}</p>
        ) : (
          <>
            <label className="field stratify-field">
              <span>{t('groups.stratify_by')}</span>
              <select className="select" value={stratifyBy} disabled={busy} onChange={(e) => changeStratification(e.target.value as 'none' | 'gender')}>
                <option value="gender">{t('groups.gender')}</option>
                <option value="none">{t('groups.none')}</option>
              </select>
            </label>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('groups.group')}</th>
                    {Object.entries(strataCounts).map(([key, count]) => (
                      <th className="num" key={key}>
                        {stratifyBy === 'gender' ? t(`respondents.${key}` as never) : t('groups.all_respondents')} ({count})
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groups.map((group, i) => (
                    <tr key={i}>
                      <td>
                        <input
                          className="input group-name-input"
                          aria-label={t('groups.group')}
                          value={group.name}
                          maxLength={80}
                          disabled={busy}
                          onChange={(e) => setGroups((current) => current.map((item, n) => (n === i ? { ...item, name: e.target.value } : item)))}
                        />
                      </td>
                      {Object.keys(strataCounts).map((key) => (
                        <td className="num" key={key}>
                          <input
                            className="input quota-input"
                            type="number"
                            min={0}
                            max={strataCounts[key]}
                            value={group.quotas[key] ?? 0}
                            disabled={busy}
                            onChange={(e) =>
                              setGroups((current) =>
                                current.map((item, n) =>
                                  n === i
                                    ? {
                                        ...item,
                                        quotas: {
                                          ...item.quotas,
                                          [key]: Math.max(0, Number(e.target.value) || 0),
                                        },
                                      }
                                    : item,
                                ),
                              )
                            }
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr>
                    <th>{t('groups.total')}</th>
                    {Object.entries(strataCounts).map(([key, count]) => (
                      <th
                        className="num"
                        key={key}
                        style={{
                          color: quotaTotals[key] === count ? undefined : 'var(--red)',
                        }}
                      >
                        {quotaTotals[key] ?? 0} / {count}
                      </th>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="btn-row">
              <button className="btn" disabled={busy || groups.length >= 20} onClick={addGroup}>
                {t('groups.add')}
              </button>
              <button
                className="btn primary"
                disabled={busy || groups.length < 2 || Object.entries(strataCounts).some(([key, count]) => quotaTotals[key] !== count) || groups.some((g) => !g.name.trim())}
                onClick={() => void randomize()}
              >
                {t('groups.randomize')}
              </button>
            </div>
            {currentAssignments.length > 0 && (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('respondents.name')}</th>
                      <th>{t('groups.group')}</th>
                      <th>{t('groups.stratum')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentAssignments.slice(0, 100).map((a) => (
                      <tr key={a.respondentId}>
                        <td>{respondentNames[a.respondentId] ?? a.respondentId}</td>
                        <td>{a.groupName}</td>
                        <td>{stratifyBy === 'gender' ? t(`respondents.${a.stratum}` as never) : t('groups.all_respondents')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {currentAssignments.length > 100 && (
              <p className="hint">
                {t('groups.showing_first', {
                  count: 100,
                  total: currentAssignments.length,
                })}
              </p>
            )}
            <p className="hint">{t('groups.lock_hint')}</p>
          </>
        )}
      </div>
    </CollapsibleCard>
  );
}
